import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Loader2, Search, Plus } from "lucide-react";
import Button from "./Button";

function LookupIdentifiersDialog({ open, onClose, universeId, onAdd }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedSecIds, setSelectedSecIds] = useState(new Set());

    const handleSearch = useCallback(async (searchQuery) => {
        if (!searchQuery || searchQuery.length < 3) {
            setResults([]);
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`/v1/universes/securities/lookup?query=${encodeURIComponent(searchQuery)}`, {
                headers: {
                    ...(token ? { "Authorization": `Bearer ${token}` } : {})
                }
            });
            if (response.ok) {
                const data = await response.json();
                setResults(data);
            }
        } catch (error) {
            console.error("Lookup error", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            handleSearch(query);
        }, 500);
        return () => clearTimeout(timer);
    }, [query, handleSearch]);

    const toggleSelection = (secId) => {
        const next = new Set(selectedSecIds);
        if (next.has(secId)) next.delete(secId);
        else next.add(secId);
        setSelectedSecIds(next);
    };

    const handleConfirmAdd = () => {
        if (selectedSecIds.size > 0) {
            onAdd(Array.from(selectedSecIds));
            setSelectedSecIds(new Set());
            onClose();
        }
    };

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setQuery("");
            setResults([]);
            setSelectedSecIds(new Set());
        }
    }, [open]);

    if (!open) return null;

    return (
        <dialog
            open
            className="fixed inset-0 z-[100] flex items-center justify-center w-screen h-screen m-0 max-w-none max-h-none border-0 bg-slate-900/60 backdrop-blur-sm backdrop-filter p-4"
            onCancel={onClose}
        >
            <div className="w-full max-w-4xl rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                <div className="border-b border-slate-200 px-5 py-4">
                    <h2 className="text-lg font-semibold text-slate-900">Look up Identifiers</h2>
                    <p className="mt-1 text-sm text-slate-600">Cerca per ISIN, SecId o Nome</p>
                </div>

                <div className="p-5 space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Digita almeno 3 caratteri..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
                        />
                    </div>

                    <div className="h-[300px] overflow-y-auto border border-slate-100 rounded-md">
                        {isLoading ? (
                            <div className="flex h-full items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                            </div>
                        ) : results.length > 0 ? (
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr className="border-b border-slate-200">
                                        <th className="px-3 py-2 text-left">ISIN</th>
                                        <th className="px-3 py-2 text-left">Nome</th>
                                        <th className="px-3 py-2 text-right">Azione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((res) => (
                                        <tr key={res.secId} className="border-b border-slate-50 hover:bg-slate-50">
                                            <td className="px-3 py-2 font-mono text-xs">{res.isin}</td>
                                            <td className="px-3 py-2">
                                                <div className="font-medium">{res.fundName || res.name}</div>
                                                <div className="text-xs text-slate-500">{res.secId}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    onClick={() => toggleSelection(res.secId)}
                                                    className={`p-1 rounded-md transition ${selectedSecIds.has(res.secId)
                                                            ? "bg-blue-500 text-white"
                                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                                        }`}
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                                {query.length >= 3 ? "Nessun risultato trovato" : "Inizia a digitare per cercare..."}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-between items-center border-t border-slate-200 bg-slate-50 px-5 py-3">
                    <span className="text-sm text-slate-600">
                        {selectedSecIds.size} security selezionate
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>Annulla</Button>
                        <Button onClick={handleConfirmAdd} disabled={selectedSecIds.size === 0}>
                            Aggiungi all'Universo
                        </Button>
                    </div>
                </div>
            </div>
        </dialog>
    );
}

LookupIdentifiersDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    universeId: PropTypes.string,
    onAdd: PropTypes.func.isRequired,
};

export default LookupIdentifiersDialog;
